/*
 * State Plane projection, client side.
 *
 * The datum shift stays on the server (it needs NADCON grids), but the
 * projection itself is pure maths and has to run locally: the cursor readout
 * converts on every mousemove, and a Lambert projection is too curved to
 * approximate across a screen — a linear fit is already ~6 ft off 5 km from
 * its anchor.
 *
 * Formulas are Snyder, "Map Projections — A Working Manual" (USGS PP 1395):
 * Lambert Conformal Conic (2SP) p. 107 and Transverse Mercator p. 61.
 * Parameters mirror the proj4 definitions in spatial_ref_sys.
 */

const FT_US_PER_METER = 1 / 0.3048006096012192;

const ELLIPSOIDS = {
  // Clarke 1866 — NAD27
  NAD27: { a: 6378206.4, f: 1 / 294.9786982 },
  // GRS80 — NAD83
  NAD83: { a: 6378137, f: 1 / 298.257222101 },
};

/* Zones covering the states the app works in. Each datum carries its own
 * parameters: the two are not variations of one grid — NAD27 and NAD83 differ
 * in central meridian for Texas North Central, and in every false origin. */
export const SPC_ZONES = [
  {
    key: "TX_N",
    label: "TX-N",
    name: "Texas North",
    NAD27: {
      srid: 32037,
      proj: "lcc",
      lat1: 34.65,
      lat2: 36.18333333333333,
      lat0: 34,
      lon0: -101.5,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 2275,
      proj: "lcc",
      lat1: 36.18333333333333,
      lat2: 34.65,
      lat0: 34,
      lon0: -101.5,
      x0: 200000.0001016002,
      y0: 999999.9998983998,
    },
  },
  {
    key: "TX_NC",
    label: "TX-NC",
    name: "Texas North Central",
    NAD27: {
      srid: 32038,
      proj: "lcc",
      lat1: 32.13333333333333,
      lat2: 33.96666666666667,
      lat0: 31.66666666666667,
      lon0: -97.5,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 2276,
      proj: "lcc",
      lat1: 33.96666666666667,
      lat2: 32.13333333333333,
      lat0: 31.66666666666667,
      lon0: -98.5,
      x0: 600000,
      y0: 2000000.0001016,
    },
  },
  {
    key: "TX_C",
    label: "TX-C",
    name: "Texas Central",
    NAD27: {
      srid: 32039,
      proj: "lcc",
      lat1: 30.11666666666667,
      lat2: 31.88333333333333,
      lat0: 29.66666666666667,
      lon0: -100.3333333333333,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 2277,
      proj: "lcc",
      lat1: 31.88333333333333,
      lat2: 30.11666666666667,
      lat0: 29.66666666666667,
      lon0: -100.3333333333333,
      x0: 699999.9998983998,
      y0: 3000000,
    },
  },
  {
    key: "TX_SC",
    label: "TX-SC",
    name: "Texas South Central",
    NAD27: {
      srid: 32040,
      proj: "lcc",
      lat1: 28.38333333333333,
      lat2: 30.28333333333333,
      lat0: 27.83333333333333,
      lon0: -99,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 2278,
      proj: "lcc",
      lat1: 30.28333333333333,
      lat2: 28.38333333333333,
      lat0: 27.83333333333333,
      lon0: -99,
      x0: 600000,
      y0: 3999999.9998984,
    },
  },
  {
    key: "TX_S",
    label: "TX-S",
    name: "Texas South",
    NAD27: {
      srid: 32041,
      proj: "lcc",
      lat1: 26.16666666666667,
      lat2: 27.83333333333333,
      lat0: 25.66666666666667,
      lon0: -98.5,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 2279,
      proj: "lcc",
      lat1: 27.83333333333333,
      lat2: 26.16666666666667,
      lat0: 25.66666666666667,
      lon0: -98.5,
      x0: 300000.0000000001,
      y0: 5000000.0001016,
    },
  },
  {
    key: "NM_E",
    label: "NM-E",
    name: "New Mexico East",
    NAD27: {
      srid: 32012,
      proj: "tmerc",
      lat0: 31,
      lon0: -104.3333333333333,
      k0: 0.999909091,
      x0: 152400.3048006096,
      y0: 0,
    },
    NAD83: {
      srid: 2257,
      proj: "tmerc",
      lat0: 31,
      lon0: -104.3333333333333,
      k0: 0.999909091,
      x0: 165000,
      y0: 0,
    },
  },
  {
    key: "NM_C",
    label: "NM-C",
    name: "New Mexico Central",
    NAD27: {
      srid: 32013,
      proj: "tmerc",
      lat0: 31,
      lon0: -106.25,
      k0: 0.9999,
      x0: 152400.3048006096,
      y0: 0,
    },
    NAD83: {
      srid: 2258,
      proj: "tmerc",
      lat0: 31,
      lon0: -106.25,
      k0: 0.9999,
      x0: 500000.0001016001,
      y0: 0,
    },
  },
  {
    key: "NM_W",
    label: "NM-W",
    name: "New Mexico West",
    NAD27: {
      srid: 32014,
      proj: "tmerc",
      lat0: 31,
      lon0: -107.8333333333333,
      k0: 0.999916667,
      x0: 152400.3048006096,
      y0: 0,
    },
    NAD83: {
      srid: 2259,
      proj: "tmerc",
      lat0: 31,
      lon0: -107.8333333333333,
      k0: 0.999916667,
      x0: 830000.0001016001,
      y0: 0,
    },
  },
  {
    key: "LA_N",
    label: "LA-N",
    name: "Louisiana North",
    NAD27: {
      srid: 26781,
      proj: "lcc",
      lat1: 31.16666666666667,
      lat2: 32.66666666666666,
      lat0: 30.66666666666667,
      lon0: -92.5,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 3451,
      proj: "lcc",
      lat1: 32.66666666666666,
      lat2: 31.16666666666667,
      lat0: 30.5,
      lon0: -92.5,
      x0: 999999.9999898402,
      y0: 0,
    },
  },
  {
    key: "LA_S",
    label: "LA-S",
    name: "Louisiana South",
    NAD27: {
      srid: 26782,
      proj: "lcc",
      lat1: 29.3,
      lat2: 30.7,
      lat0: 28.66666666666667,
      lon0: -91.33333333333333,
      x0: 609601.2192024384,
      y0: 0,
    },
    NAD83: {
      srid: 3452,
      proj: "lcc",
      lat1: 30.7,
      lat2: 29.3,
      lat0: 28.5,
      lon0: -91.33333333333333,
      x0: 999999.9999898402,
      y0: 0,
    },
  },
];

export const DEFAULT_SPC_ZONE = "TX_C";

export const SPC_ZONE_OPTIONS = SPC_ZONES.map((zone) => ({
  value: zone.key,
  label: zone.label,
  title: zone.name,
}));

const findZone = (zoneKey) =>
  SPC_ZONES.find((zone) => zone.key === zoneKey) || null;

const rad = (degrees) => (degrees * Math.PI) / 180;

const lccForward = (lat, lng, p, ellipsoid) => {
  const { a, f } = ellipsoid;
  const e = Math.sqrt(2 * f - f * f);

  const m = (phi) =>
    Math.cos(phi) / Math.sqrt(1 - e * e * Math.sin(phi) * Math.sin(phi));

  const t = (phi) =>
    Math.tan(Math.PI / 4 - phi / 2) /
    Math.pow((1 - e * Math.sin(phi)) / (1 + e * Math.sin(phi)), e / 2);

  const phi1 = rad(p.lat1);
  const phi2 = rad(p.lat2);
  const phi0 = rad(p.lat0);
  const phi = rad(lat);

  const n =
    Math.abs(phi1 - phi2) < 1e-10
      ? Math.sin(phi1)
      : (Math.log(m(phi1)) - Math.log(m(phi2))) /
        (Math.log(t(phi1)) - Math.log(t(phi2)));

  const F = m(phi1) / (n * Math.pow(t(phi1), n));
  const rho0 = a * F * Math.pow(t(phi0), n);
  const rho = a * F * Math.pow(t(phi), n);
  const theta = n * rad(lng - p.lon0);

  return {
    x: p.x0 + rho * Math.sin(theta),
    y: p.y0 + rho0 - rho * Math.cos(theta),
  };
};

const tmercForward = (lat, lng, p, ellipsoid) => {
  const { a, f } = ellipsoid;
  const es = 2 * f - f * f;
  const eps = es / (1 - es);

  const phi = rad(lat);
  const phi0 = rad(p.lat0);

  const meridionalArc = (angle) =>
    a *
    ((1 - es / 4 - (3 * es * es) / 64 - (5 * es ** 3) / 256) * angle -
      ((3 * es) / 8 + (3 * es * es) / 32 + (45 * es ** 3) / 1024) *
        Math.sin(2 * angle) +
      ((15 * es * es) / 256 + (45 * es ** 3) / 1024) * Math.sin(4 * angle) -
      ((35 * es ** 3) / 3072) * Math.sin(6 * angle));

  const N = a / Math.sqrt(1 - es * Math.sin(phi) * Math.sin(phi));
  const T = Math.tan(phi) ** 2;
  const C = eps * Math.cos(phi) ** 2;
  const A = rad(lng - p.lon0) * Math.cos(phi);

  const x =
    p.x0 +
    p.k0 *
      N *
      (A +
        ((1 - T + C) * A ** 3) / 6 +
        ((5 - 18 * T + T * T + 72 * C - 58 * eps) * A ** 5) / 120);

  const y =
    p.y0 +
    p.k0 *
      (meridionalArc(phi) -
        meridionalArc(phi0) +
        N *
          Math.tan(phi) *
          ((A * A) / 2 +
            ((5 - T + 9 * C + 4 * C * C) * A ** 4) / 24 +
            ((61 - 58 * T + T * T + 600 * C - 330 * eps) * A ** 6) / 720));

  return { x, y };
};

/* Projects a coordinate already expressed in `datum` (NAD27 or NAD83) into
 * that datum's State Plane zone. Returns feet — every zone here is US survey
 * feet, which is also what plats print. */
export const toStatePlane = ({ lat, lng }, zoneKey, datum) => {
  const zone = findZone(zoneKey);
  const params = zone?.[datum];
  const ellipsoid = ELLIPSOIDS[datum];

  if (!params || !ellipsoid) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const projected =
    params.proj === "tmerc"
      ? tmercForward(lat, lng, params, ellipsoid)
      : lccForward(lat, lng, params, ellipsoid);

  return {
    x: projected.x * FT_US_PER_METER,
    y: projected.y * FT_US_PER_METER,
  };
};
